<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  computed,
  markRaw,
  onMounted,
  ref,
  watch
} from 'vue';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import {
  formatCurrency,
  formatDate,
  formatItemValue,
  formatPercentage
} from '@/utils/formatters.ts';
import { useAuthStore } from "@/stores/authStore.ts";
import type {
  MandateFeesPeriod,
  MandateFeesRowResponse,
  MandateFeesSortKey
} from "@/types/services/fees/mandateFees.type.ts";
import { MandateFeesPeriodKeys } from "@/types/services/fees/mandateFees.type.ts";
import FeesService from "@/services/fees/feesService.ts";
import {
  ExportFormatKey,
  FeesPeriodSearchKey
} from "@/enums/fees_enum.ts";
import { displayModal } from "@/utils/modalUtils.ts";
import { displayToast } from "@/utils/toastUtils.ts";

import type {
  PaginatedPeriodQuery,
  SortItem,
  SortOrder
} from '@/types/services/utils/request.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type { PaginatedResponse } from "@/types/services/utils/response.type.ts";
import ExportFeesComponent from "@/components/fees/ExportFeesComponent.vue";

const { t } = useI18n();
const authStore = useAuthStore()

const feesItems = ref<MandateFeesRowResponse[]>([]);
const loading = ref<boolean>(false);

// Period
const periodItems = computed(() =>
  MandateFeesPeriodKeys.map((key) => ({
    title: t(`views.mandateFees.period.keys.${key}`),
    value: key,
  }))
);
const periodSearch = ref<MandateFeesPeriod>(FeesPeriodSearchKey.MONTHLY);
const periodMenu = ref<boolean>(false);
const periodPickerModel = ref<(string | null)[]>([]);
const formattedPickerPeriod = computed(() => {
  const start = periodPickerModel.value[0];
  const last = periodPickerModel.value[periodPickerModel.value.length - 1];
  const end = start !== last ? last : null;

  return {
    start: start ? formatDate(start) : '',
    end: end ? formatDate(end) : '',
  }
});
const isCustomPeriodValid = computed(() => {
  return (
    periodSearch.value === FeesPeriodSearchKey.CUSTOM &&
    formattedPickerPeriod.value.start &&
    formattedPickerPeriod.value.end
  );
});

// Sorting and pagination
const sort = ref<MandateFeesSortKey>("mandateName");
const order = ref<SortOrder>("desc");
const limit = ref<number>(10);
const page = ref<number>(1);

const total = ref<number>(0);
const maxPages = ref<number>(1);

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('views.mandateFees.header.mandateName'),
    key: 'mandateName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandateFees.header.investedAmount'),
    key: 'investedAmount',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandateFees.header.percentage'),
    key: 'percentage',
    sortable: true,
    align: 'start'
  },
  {
    title: t('views.mandateFees.header.feesAmount'),
    key: 'feesAmount',
    sortable: true,
    align: 'start'
  },
]);

onMounted(fetchMandateFees);

watch([periodSearch, sort, order, limit, page], () => {
  if (periodSearch.value !== FeesPeriodSearchKey.CUSTOM) {
    fetchMandateFees();
  }
});

watch(periodPickerModel, () => {
  if (isCustomPeriodValid.value) {
    fetchMandateFees();
  }
}, { deep: true });

async function fetchMandateFees(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedPeriodQuery = {
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value,
      period: periodSearch.value === FeesPeriodSearchKey.CUSTOM
        ? `${new Date(periodPickerModel.value[0] as string).toISOString()} ${new Date(periodPickerModel.value[periodPickerModel.value.length - 1] as string).toISOString()}`
        : periodSearch.value
    };

    const response: PaginatedResponse<MandateFeesRowResponse> =
      await FeesService.getMandateFeesPaginated(params);

    feesItems.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<MandateFeesSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

function clearPeriodSearchField(): void {
  periodPickerModel.value = [];
  periodSearch.value = FeesPeriodSearchKey.MONTHLY;
}

async function handleExportFees(): Promise<void> {
  if (periodSearch.value === FeesPeriodSearchKey.CUSTOM
    && (!periodPickerModel.value[0] || !periodPickerModel.value[periodPickerModel.value.length - 1])) {
    return;
  }

  const modal = await displayModal({
    component: markRaw(ExportFeesComponent),
    props: {
      title: t('views.mandateFees.modal.cancel.title'),
      message: t('views.mandateFees.modal.cancel.message'),
    },
  }) as string | null;

  if (typeof modal === 'string' && modal.length > 0 && Object.values(ExportFormatKey).includes(modal as ExportFormatKey)) {
    try {
      loading.value = true;

      await FeesService.exportMyFees(
        periodSearch.value === FeesPeriodSearchKey.CUSTOM
        ? `${new Date(periodPickerModel.value[0] as string).toISOString()} ${new Date(periodPickerModel.value[periodPickerModel.value.length - 1] as string).toISOString()}`
        : periodSearch.value,
        modal as ExportFormatKey
      );

      displayToast(t('views.mandateFees.cancelSuccess'), 'success');
    } finally {
      loading.value = false;
    }
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhReceipt">
    {{ t("views.mandateFees.title") }}
  </MainTitleComponent>

  <v-row align="center" justify="space-between">
    <!-- Period field -->
    <v-col cols="12" md="7">
      <v-row align="center">
        <v-col cols="12" md="6">
          <v-select
            v-model="periodSearch"
            :items="periodItems"
            :label="t('views.mandateFees.period.title')"
            append-inner-icon="PhCalendar"
            density="compact"
            hide-details
          />
        </v-col>

        <!-- Date selection fields -->
        <v-col v-if="periodSearch === 'custom'" cols="12" md="6">
          <v-menu v-model="periodMenu" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-text-field
                :label="t('views.mandateFees.period.dates')"
                :model-value="`${formattedPickerPeriod.start}${formattedPickerPeriod.start ? ' → ' : ''}${formattedPickerPeriod.end}`"
                clearable
                density="compact"
                hide-details
                readonly
                v-bind="props"
                @click:clear="clearPeriodSearchField"
              />
            </template>

            <v-card>
              <v-date-picker
                v-model="periodPickerModel"
                multiple="range"
                show-adjacent-months
              />
              <v-card-actions>
                <v-spacer />
                <v-btn
                  text="OK"
                  variant="text"
                  @click="periodMenu = false"
                />
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-col>
      </v-row>
    </v-col>

    <!-- Export button -->
    <v-col v-if="hasPermission(['GET_MANDATE_FEES', 'GET_ADMIN_MANDATE_FEES'])" cols="12" sm="auto">
      <v-btn
        :loading="loading"
        class="w-100 w-sm-auto"
        @click="handleExportFees"
      >
        {{ t("views.mandateFees.buttons.export") }}
        <template #prepend>
          <v-icon icon="PhDownload" />
        </template>
      </v-btn>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="feesItems"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.mandateName`]="{ item }">
          {{ formatItemValue(item.mandateName) }}
        </template>
        <template #[`item.investedAmount`]="{ item }">
          {{ formatCurrency(item.investedAmount) }}
        </template>
        <template #[`item.percentage`]="{ item }">
          {{ formatPercentage(item.percentage) }} %
        </template>
        <template #[`item.feesAmount`]="{ item }">
          {{ formatCurrency(item.feesAmount) }}
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
